import { useMemo } from "react"

import { primitives } from "@coval/ui/tokens"
import { useTheme } from "next-themes"

/**
 * The well's 60px box split between the two speakers, so each lane fills
 * exactly the half of the well its background band covers.
 */
const SPLIT_CHANNEL_HEIGHT_PIXELS = 30
const SPLIT_CHANNEL_COUNT = 2
const COMPACT_HEIGHT_PIXELS = 35

/** Which recorded channel carries which side of the conversation. */
export const AGENT_CHANNEL_INDEX = 0
export const PERSONA_CHANNEL_INDEX = 1

/**
 * The full player draws into a fixed-height well that clips whatever overflows
 * it, and the region grips and labels size themselves off the canvas rather
 * than the well. Handing Wavesurfer the height the lanes actually occupy keeps
 * all three inside the well, including for mono audio, which renders a single
 * lane at this height instead of the split pair.
 */
const FULL_HEIGHT_PIXELS = SPLIT_CHANNEL_HEIGHT_PIXELS * SPLIT_CHANNEL_COUNT

/**
 * What a player draws with when it draws one wave rather than a lane per
 * speaker — the compact player, and mono audio in the full one — plus the
 * cursor, which crosses both lanes and so belongs to neither. Unplayed audio
 * sits close to the well and played audio pulls away from it, which is what
 * carries progress.
 *
 * These are keyed to the page theme, not to the well's own subtree: Wavesurfer
 * paints a canvas and takes literal colour strings, so it cannot read the token
 * the way the grips do. The two agree on every current call site, none of which
 * puts the player inside a .coval-theme-invert subtree. A caller that wants one
 * has to resolve these off the well element instead.
 */
const WAVEFORM_COLORS = {
  light: {
    cursor: primitives.primaryWarm[900],
    progress: primitives.primaryWarm[800],
    wave: primitives.primaryWarm[300],
  },
  dark: {
    cursor: primitives.primaryWarm[0],
    progress: primitives.primaryWarm[100],
    wave: primitives.primaryWarm[600],
  },
} as const

/** How much of its played colour unplayed audio keeps. */
const UNPLAYED_STRENGTH = 0.2

/** `foreground` at `foregroundAlpha` over an opaque `background`, flattened. */
function flattenHex(
  foreground: string,
  background: string,
  foregroundAlpha: number,
): string {
  const channels = [1, 3, 5].map((offset) => {
    const front = parseInt(foreground.slice(offset, offset + 2), 16)
    const back = parseInt(background.slice(offset, offset + 2), 16)
    return Math.round(front * foregroundAlpha + back * (1 - foregroundAlpha))
  })

  return `#${channels.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`
}

/**
 * The played colour each speaker's lane draws in. The agent's is drawn on
 * --coval-chat-agent-surface rather than on the media track, so it takes its
 * blue off that band's own ramp instead of the warm neutral one. Resolved here
 * for the same reason the neutrals above are — Wavesurfer takes colour strings,
 * not tokens.
 */
const LANE_PLAYED_COLORS = {
  light: {
    [AGENT_CHANNEL_INDEX]: primitives.sky[800],
    [PERSONA_CHANNEL_INDEX]: primitives.primaryWarm[800],
  },
  dark: {
    [AGENT_CHANNEL_INDEX]: primitives.sky[200],
    [PERSONA_CHANNEL_INDEX]: primitives.primaryWarm[100],
  },
} as const

/**
 * The band each lane's wave is drawn onto — what --coval-chat-agent-surface and
 * --coval-media-track resolve to for this theme. The dark agent band is a
 * translucent tint in the stylesheet, so its flattened form is repeated here.
 */
const LANE_BAND_COLORS = {
  light: {
    [AGENT_CHANNEL_INDEX]: primitives.sky[100],
    [PERSONA_CHANNEL_INDEX]: primitives.primaryWarm[50],
  },
  dark: {
    [AGENT_CHANNEL_INDEX]: flattenHex(
      primitives.sky[900],
      primitives.primaryWarm[900],
      0.4,
    ),
    [PERSONA_CHANNEL_INDEX]: primitives.primaryWarm[800],
  },
} as const

/**
 * The colours Wavesurfer is handed, plus the height and two-lane split the full
 * player draws in. Wavesurfer takes literal colour strings rather than CSS
 * variables, so these resolve the token values here instead of in the
 * stylesheet.
 */
export function useAudioWaveformOptions(compact: boolean) {
  const { resolvedTheme } = useTheme()
  const themeKey = resolvedTheme === "dark" ? "dark" : "light"
  const { cursor, progress, wave } = WAVEFORM_COLORS[themeKey]
  const lanePlayedColors = LANE_PLAYED_COLORS[themeKey]
  const laneBandColors = LANE_BAND_COLORS[themeKey]

  const splitChannels = useMemo(
    () =>
      compact
        ? undefined
        : Array.from({ length: SPLIT_CHANNEL_COUNT }, (_unused, channel) => {
            const lane = channel as keyof typeof lanePlayedColors
            const played = lanePlayedColors[lane]

            return {
              height: SPLIT_CHANNEL_HEIGHT_PIXELS,
              normalize: true,
              // Unplayed audio is its own played colour sunk into the band
              // behind it, so progress reads as the wave filling in rather than
              // as a change of hue. Flattened to an opaque colour rather than
              // given an alpha: Wavesurfer builds the played canvas by masking a
              // copy of this one, which multiplies the played colour by whatever
              // alpha this one carries — a translucent wave would take the
              // played wave down with it.
              waveColor: flattenHex(
                played,
                laneBandColors[lane],
                UNPLAYED_STRENGTH,
              ),
              progressColor: played,
            }
          }),
    [compact, laneBandColors, lanePlayedColors],
  )

  return {
    cursorColor: cursor,
    height: compact ? COMPACT_HEIGHT_PIXELS : FULL_HEIGHT_PIXELS,
    laneHeight: SPLIT_CHANNEL_HEIGHT_PIXELS,
    progressColor: progress,
    splitChannels,
    waveColor: wave,
  }
}
